import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Paper,
  Box,
  Button,
  Typography,
  Stack,
  TextField,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link
} from '@mui/material';
import axiosInstance from '../utils/axiosInstance';

const ApplyInputPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { application_id } = location.state || {};

  const [loading, setLoading] = useState(false);

  const { handleSubmit, control, formState: { errors } } = useForm({
    defaultValues: {
      organization_name: '',
      admin_last_name: '',
      admin_first_name: '',
      phone_number: '',
      agree_terms: false,
      agree_privacy: false
    }
  });

  useEffect(() => {
    if (!application_id) {
      navigate('/', { replace: true });
    }
  }, [application_id, navigate]);

  const onSubmit = async (data) => {
    if (loading) return;
    try {
      setLoading(true);
      const res = await axiosInstance.put('/application', {
        application_id,
        ...data
      });

      navigate('/apply/sent', {
        state: {
          application_id,
          plan_id: res.data.plan_id,
          email: res.data.admin_email
        }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto' }}>
      <Typography component="h1" variant="h2" align="center" mb={3}>
        30日間無料トライアル
      </Typography>

      <Paper variant='section' sx={{ p: 3 }}>
        <Typography mb={2}>
          下記の必要事項を入力してください。
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            {/* 組織名 */}
            <Controller
              name="organization_name"
              control={control}
              rules={{
                required: '組織名を入力してください',
                maxLength: { value: 100, message: '100文字以内で入力してください' }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="組織名 *"
                  placeholder="株式会社〇〇"
                  error={!!errors.organization_name}
                  helperText={errors.organization_name?.message}
                  inputProps={{ maxLength: 100 }}
                />
              )}
            />

            {/* 氏名 */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="admin_last_name"
                control={control}
                rules={{
                  required: '姓を入力してください',
                  maxLength: { value: 50, message: '50文字以内で入力してください' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="氏名（姓） *"
                    placeholder="山田"
                    error={!!errors.admin_last_name}
                    helperText={errors.admin_last_name?.message}
                    inputProps={{ maxLength: 50 }}
                  />
                )}
              />
              <Controller
                name="admin_first_name"
                control={control}
                rules={{
                  required: '名を入力してください',
                  maxLength: { value: 50, message: '50文字以内で入力してください' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="氏名（名） *"
                    placeholder="太郎"
                    error={!!errors.admin_first_name}
                    helperText={errors.admin_first_name?.message}
                    inputProps={{ maxLength: 50 }}
                  />
                )}
              />
            </Stack>

            {/* 電話番号 */}
            <Controller
              name="phone_number"
              control={control}
              rules={{
                maxLength: { value: 20, message: '20文字以内で入力してください' },
                pattern: { value: /^(?:\+?\d{1,4}[-\s]?)?0?\d{1,4}[-\s]?\d{1,4}[-\s]?\d{4}$/, message: '電話番号形式で入力してください' }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="電話番号"
                  placeholder="090-1234-5678"
                  error={!!errors.phone_number}
                  helperText={errors.phone_number?.message}
                  inputProps={{ maxLength: 20 }}
                />
              )}
            />

            {/* 利用規約同意 */}
            <Controller
              name="agree_terms"
              control={control}
              rules={{ required: '利用規約へ同意が必要です' }}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label={
                    <Link href="https://nocstela.com/tendenec/terms-of-use/" target="_blank" rel="noopener">
                      利用規約に同意する *
                    </Link>
                  }
                />
              )}
            />
            {errors.agree_terms && (
              <Typography variant="body2" color="error">
                {errors.agree_terms.message}
              </Typography>
            )}

            {/* プライバシーポリシー同意 */}
            <Controller
              name="agree_privacy"
              control={control}
              rules={{ required: 'プライバシーポリシーへの同意が必要です' }}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label={
                    <Link href="https://nocstela.com/privacy-policy/" target="_blank" rel="noopener">
                      プライバシーポリシーに同意する *
                    </Link>
                  }
                />
              )}
            />
            {errors.agree_privacy && (
              <Typography variant="body2" color="error">
                {errors.agree_privacy.message}
              </Typography>
            )}

            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : '送信'}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default ApplyInputPage;